import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { css, cx } from '@emotion/css';
import { theme, expandFontTheme, mapCssValue, useThemeContext, getThemeColor, } from '@contrast-security-inc/design-system-foundations';
import { Tooltip } from '../Tooltip/Tooltip';
import { Text } from '@contrast-security-inc/design-system-typography';
const textOverflowStyles = (maxWidth) => css `
  display: block;
  max-width: ${maxWidth ? mapCssValue(maxWidth) : '100%'};
  white-space: nowrap;
`;
const textOverflowWrapperStyles = (color, font, selectedTheme) => css `
  ${font && expandFontTheme(theme.font[font])};
  color: ${color ? theme.color[color] : getThemeColor(selectedTheme, { light: 'inherit', dark: theme.color.white })};
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;
const textOverflowTextStyles = css `
  display: unset;
`;
export const TextOverflow = forwardRef(({ as, color, font, hasArrow = false, placement = 'top-start', offset = [-6, 0], maxWidth, testId, children, ...rest }, ref) => {
    const { selectedTheme } = useThemeContext();
    const testIdBase = `${testId ?? 'textoverflow'}`;
    const [isOverflowing, setIsOverflowing] = useState(false);
    const itemRef = useRef(null);
    const wrapperRef = useRef(null);
    const themedColor = selectedTheme === 'dark' ? 'white' : undefined;
    const colorToUse = color ?? themedColor;
    const checkOverflow = useCallback(() => {
        if (wrapperRef.current && itemRef.current) {
            const wrapperWidth = wrapperRef.current.getBoundingClientRect().width ?? 0;
            const textWidth = itemRef.current.getBoundingClientRect().width ?? 0;
            setIsOverflowing(textWidth > wrapperWidth);
        }
    }, [wrapperRef, itemRef]);
    useLayoutEffect(() => {
        checkOverflow();
    }, [checkOverflow, children]);
    useEffect(() => {
        window.addEventListener('resize', checkOverflow);
        return () => {
            window.removeEventListener('resize', checkOverflow);
        };
    }, [checkOverflow]);
    return (React.createElement("span", { ref: ref, "data-testid": testIdBase, ...rest, className: cx('ds-textoverflow', textOverflowStyles(maxWidth), rest?.className) },
        React.createElement("span", { ref: wrapperRef, className: textOverflowWrapperStyles(color, font, selectedTheme), "data-testid": `${testIdBase}-wrapper` },
            React.createElement("span", { ref: itemRef, "data-testid": `${testIdBase}-inner-wrapper` }, isOverflowing ? (React.createElement(Tooltip, { placement: placement, hasArrow: hasArrow, offset: offset, body: children, testId: `${testIdBase}-tooltip` },
                React.createElement(Text, { className: textOverflowTextStyles, as: as, font: font, color: colorToUse, testId: `${testIdBase}-overflowing-text` }, children))) : (React.createElement(Text, { className: textOverflowTextStyles, as: as, font: font, color: colorToUse, testId: `${testIdBase}-text` }, children))))));
});
TextOverflow.displayName = 'TextOverflow';
